import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import { IconArrowLeft, IconBan, IconCheck, IconEdit, IconPlus, IconTrash } from "@tabler/icons-react"
import { useState } from "react"

const BALANCE_TYPES = ["holiday", "parental", "study", "unpaid"] as const

interface Balance {
  id: number
  balance_type: string
  year: number
  days_available: number
  days_used: number
  remaining_days: number
}

interface SalaryHistory {
  id: number
  effective_date: string
  seniority_level_id: number
  seniority_title: string
  contract_hours: number
  bonus: number
  notes: string | null
  salary: string
  salary_change: string
  salary_increase: number
  /** Raw, as typed — what the edit form prefills with. */
  reason: string
  /** The reason plus the level it moved them to, when it changed. */
  reason_display: string
  created_by: string
}

interface SeniorityLevelOption {
  id: number
  name: string
}

interface SalaryFormData {
  effective_date: string
  seniority_level_id: string
  contract_hours: string
  bonus: string
  reason: string
  notes: string
}

const emptySalaryForm: SalaryFormData = {
  effective_date: "",
  seniority_level_id: "",
  contract_hours: "8",
  bonus: "0",
  reason: "",
  notes: "",
}

interface TimeOffRequest {
  id: number
  request_type: string
  days_requested: number
  start_date: string
  end_date: string
  status: string
}

interface BillCost {
  bill_type: string
  bill_type_display: string
  currency: string
  total: string
}

interface UserDetail {
  id: number
  name: string
  email: string
  role: string
  active: boolean
  avatar_url: string | null
  hire_date: string | null
  last_day_worked: string | null
  reason_for_leaving: string | null
  national_id: string | null
  rut: string | null
  date_of_birth: string | null
  updated_at: string
  current_salary_display: string
  seniority_title: string
  contract_hours_per_day: number
  is_current_user: boolean
  balances: Balance[]
  balance_summary: {
    total_available: number
    total_used: number
    total_remaining: number
  }
  salary_histories: SalaryHistory[]
  recent_requests: TimeOffRequest[]
  bills_by_type: BillCost[]
}

interface Props {
  [key: string]: unknown
  user: UserDetail
  seniority_levels: SeniorityLevelOption[]
}

function DetailCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm font-medium">{children}</dd>
    </div>
  )
}

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  approved: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  denied: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  cancelled: "bg-muted text-muted-foreground",
}

interface BalanceFormData {
  balance_type: string
  year: string
  days_available: string
  days_used: string
}

const emptyBalanceForm: BalanceFormData = {
  balance_type: "holiday",
  year: new Date().getFullYear().toString(),
  days_available: "",
  days_used: "0",
}

export default function AdminUserShow() {
  const { user, seniority_levels } = usePage<Props>().props
  const [showDeactivateModal, setShowDeactivateModal] = useState(false)
  const [lastDayWorked, setLastDayWorked] = useState(new Date().toISOString().split("T")[0])
  const [reason, setReason] = useState("")
  const [processing, setProcessing] = useState(false)

  // Balance form state
  const [balanceModalOpen, setBalanceModalOpen] = useState(false)
  const [balanceModalMode, setBalanceModalMode] = useState<"create" | "edit">("create")
  const [editingBalanceId, setEditingBalanceId] = useState<number | null>(null)
  const [balanceForm, setBalanceForm] = useState<BalanceFormData>(emptyBalanceForm)
  const [balanceErrors, setBalanceErrors] = useState<Record<string, string>>({})
  const [balanceProcessing, setBalanceProcessing] = useState(false)

  const [salaryModalOpen, setSalaryModalOpen] = useState(false)
  const [editingSalaryId, setEditingSalaryId] = useState<number | null>(null)
  const [salaryForm, setSalaryForm] = useState<SalaryFormData>(emptySalaryForm)
  const [salaryErrors, setSalaryErrors] = useState<Record<string, string>>({})
  const [salaryProcessing, setSalaryProcessing] = useState(false)

  // Delete balance state
  const [deleteBalanceId, setDeleteBalanceId] = useState<number | null>(null)
  const [deleteProcessing, setDeleteProcessing] = useState(false)

  function openCreateBalance() {
    setBalanceForm(emptyBalanceForm)
    setBalanceErrors({})
    setBalanceModalMode("create")
    setEditingBalanceId(null)
    setBalanceModalOpen(true)
  }

  function openEditBalance(balance: Balance) {
    setBalanceForm({
      balance_type: balance.balance_type,
      year: balance.year.toString(),
      days_available: balance.days_available.toString(),
      days_used: balance.days_used.toString(),
    })
    setBalanceErrors({})
    setBalanceModalMode("edit")
    setEditingBalanceId(balance.id)
    setBalanceModalOpen(true)
  }

  function handleBalanceSubmit(e: React.FormEvent) {
    e.preventDefault()
    setBalanceProcessing(true)
    setBalanceErrors({})

    const payload = {
      time_off_balance: {
        balance_type: balanceForm.balance_type,
        year: Number.parseInt(balanceForm.year),
        days_available: Number.parseFloat(balanceForm.days_available),
        days_used: Number.parseFloat(balanceForm.days_used),
      },
    }

    if (balanceModalMode === "create") {
      router.post(`/admin/users/${user.id}/time_off_balances`, payload, {
        onFinish: () => setBalanceProcessing(false),
        onSuccess: () => setBalanceModalOpen(false),
        onError: (errors) => setBalanceErrors(errors),
      })
    } else {
      router.patch(`/admin/users/${user.id}/time_off_balances/${editingBalanceId}`, payload, {
        onFinish: () => setBalanceProcessing(false),
        onSuccess: () => setBalanceModalOpen(false),
        onError: (errors) => setBalanceErrors(errors),
      })
    }
  }

  function openNewSalary() {
    setSalaryForm({ ...emptySalaryForm, contract_hours: String(user.contract_hours_per_day) })
    setSalaryErrors({})
    setEditingSalaryId(null)
    setSalaryModalOpen(true)
  }

  function openEditSalary(history: SalaryHistory) {
    setSalaryForm({
      effective_date: history.effective_date,
      seniority_level_id: String(history.seniority_level_id),
      contract_hours: String(history.contract_hours),
      bonus: String(history.bonus),
      reason: history.reason,
      notes: history.notes ?? "",
    })
    setSalaryErrors({})
    setEditingSalaryId(history.id)
    setSalaryModalOpen(true)
  }

  function handleSalarySubmit(e: React.FormEvent) {
    e.preventDefault()
    setSalaryProcessing(true)
    setSalaryErrors({})

    const payload = {
      salary_history: {
        effective_date: salaryForm.effective_date,
        seniority_level_id: Number.parseInt(salaryForm.seniority_level_id),
        contract_hours: Number.parseFloat(salaryForm.contract_hours),
        bonus: Number.parseFloat(salaryForm.bonus || "0"),
        reason: salaryForm.reason,
        notes: salaryForm.notes,
      },
    }

    const options = {
      onFinish: () => setSalaryProcessing(false),
      onSuccess: () => setSalaryModalOpen(false),
      onError: (errors: Record<string, string>) => setSalaryErrors(errors),
    }

    if (editingSalaryId === null) {
      router.post(`/admin/users/${user.id}/salary_histories`, payload, options)
    } else {
      router.patch(`/admin/users/${user.id}/salary_histories/${editingSalaryId}`, payload, options)
    }
  }

  function handleDeleteBalance() {
    if (!deleteBalanceId) return
    setDeleteProcessing(true)
    router.delete(`/admin/users/${user.id}/time_off_balances/${deleteBalanceId}`, {
      onFinish: () => {
        setDeleteProcessing(false)
        setDeleteBalanceId(null)
      },
    })
  }

  function handleDeactivate(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    router.patch(
      `/admin/users/${user.id}/deactivate`,
      {
        last_day_worked: lastDayWorked,
        reason_for_leaving: reason,
      },
      {
        onFinish: () => setProcessing(false),
        onSuccess: () => setShowDeactivateModal(false),
      },
    )
  }

  function handleReactivate() {
    router.patch(`/admin/users/${user.id}/reactivate`)
  }

  const balanceBeingDeleted = user.balances.find((b) => b.id === deleteBalanceId)

  return (
    <>
      <Head title={`${user.name} - Admin - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/admin/users">Users</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{user.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                <a href="/admin/users">
                  <IconArrowLeft className="h-4 w-4" />
                </a>
              </Button>
              <div className="flex items-center gap-3">
                <Avatar size="lg" className="rounded-full">
                  {user.avatar_url ? <AvatarImage src={user.avatar_url} alt={user.name} /> : null}
                  <AvatarFallback className="rounded-full">{user.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <h1 className="text-lg font-semibold tracking-tight">
                    {user.name}
                    {user.is_current_user && <span className="ml-2 text-sm text-muted-foreground">(You)</span>}
                  </h1>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <a href={`/admin/users/${user.id}/edit`}>
                  <IconEdit className="mr-2 h-4 w-4" />
                  Edit User
                </a>
              </Button>
              {!user.is_current_user &&
                (user.active ? (
                  <Button variant="destructive" size="sm" onClick={() => setShowDeactivateModal(true)}>
                    <IconBan className="mr-2 h-4 w-4" />
                    Deactivate
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={handleReactivate}
                  >
                    <IconCheck className="mr-2 h-4 w-4" />
                    Reactivate
                  </Button>
                ))}
            </div>
          </div>
        </div>

        {/* User Info */}
        <Card>
          <CardContent>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
              <DetailCell label="Email">{user.email}</DetailCell>
              <DetailCell label="Role">
                <Badge variant={user.role === "admin" ? "default" : "secondary"} className="capitalize">
                  {user.role}
                </Badge>
              </DetailCell>
              <DetailCell label="Status">
                <Badge variant={user.active ? "default" : "secondary"} className={user.active ? "bg-green-600" : ""}>
                  {user.active ? "Active" : "Inactive"}
                </Badge>
              </DetailCell>
              {!user.active && (
                <>
                  <DetailCell label="Last Day Worked">{user.last_day_worked || "Not set"}</DetailCell>
                  <div className="sm:col-span-2">
                    <DetailCell label="Reason for Leaving">{user.reason_for_leaving || "Not set"}</DetailCell>
                  </div>
                </>
              )}
              <DetailCell label="Hire Date">{user.hire_date || "Not set"}</DetailCell>
              <DetailCell label="Last Updated">{user.updated_at}</DetailCell>
              <DetailCell label="National ID">{user.national_id || "Not set"}</DetailCell>
              <DetailCell label="RUT">{user.rut || "Not set"}</DetailCell>
              <DetailCell label="Date of Birth">{user.date_of_birth || "Not set"}</DetailCell>
              <DetailCell label="Current Salary">
                <span className="font-semibold">{user.current_salary_display}</span>
              </DetailCell>
              <DetailCell label="Seniority Level">{user.seniority_title}</DetailCell>
              <DetailCell label="Contract Hours/Day">{user.contract_hours_per_day} hours</DetailCell>
            </dl>
          </CardContent>
        </Card>

        {/* Balance Summary Cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Card>
            <CardContent>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Total Available</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {user.balance_summary.total_available}{" "}
                <span className="text-sm font-normal text-muted-foreground">days</span>
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Total Used</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {user.balance_summary.total_used}{" "}
                <span className="text-sm font-normal text-muted-foreground">days</span>
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Remaining</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {user.balance_summary.total_remaining}{" "}
                <span className="text-sm font-normal text-muted-foreground">days</span>
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Time Off Balances */}
        <Card>
          <CardHeader>
            <CardTitle>Time Off Balances</CardTitle>
            <CardAction>
              <Button size="sm" onClick={openCreateBalance}>
                <IconPlus className="mr-2 h-4 w-4" />
                Add Balance
              </Button>
            </CardAction>
          </CardHeader>
          <CardContent>
            {user.balances.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {user.balances.map((balance) => (
                  <Card key={balance.id} className="py-4 shadow-none">
                    <CardContent className="px-4">
                      <div className="mb-3 flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium capitalize">{balance.balance_type}</h4>
                          <span className="text-xs text-muted-foreground">{balance.year}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-xs"
                            onClick={() => openEditBalance(balance)}
                          >
                            Edit
                          </Button>
                          <span className="text-muted-foreground">|</span>
                          <Button
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-xs text-destructive hover:text-destructive"
                            onClick={() => setDeleteBalanceId(balance.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Available:</span>
                          <span className="font-medium tabular-nums">{balance.days_available} days</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Used:</span>
                          <span className="font-medium tabular-nums">{balance.days_used} days</span>
                        </div>
                        <Separator />
                        <div className="flex justify-between">
                          <span className="font-medium">Remaining:</span>
                          <span className="font-bold text-primary tabular-nums">{balance.remaining_days} days</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-sm font-medium">No balances configured</p>
                <p className="mt-1 text-sm text-muted-foreground">Get started by adding a time off balance.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Salary History */}
        <Card>
          <CardHeader>
            <CardTitle>Salary History</CardTitle>
            <CardAction>
              <Button size="sm" onClick={openNewSalary}>
                <IconPlus className="mr-2 h-4 w-4" />
                Add Salary Change
              </Button>
            </CardAction>
          </CardHeader>
          <CardContent>
            {user.salary_histories.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Effective Date</TableHead>
                      <TableHead>Seniority Level</TableHead>
                      <TableHead>Salary</TableHead>
                      <TableHead>Change</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Created By</TableHead>
                      <TableHead className="w-16 text-right">
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {user.salary_histories.map((history) => (
                      <TableRow key={history.id}>
                        <TableCell className="whitespace-nowrap">{history.effective_date}</TableCell>
                        <TableCell>{history.seniority_title}</TableCell>
                        <TableCell className="font-medium tabular-nums">{history.salary}</TableCell>
                        <TableCell>
                          <span
                            className={
                              history.salary_increase > 0
                                ? "font-medium text-green-600 dark:text-green-400"
                                : history.salary_increase < 0
                                  ? "font-medium text-red-600 dark:text-red-400"
                                  : "text-muted-foreground"
                            }
                          >
                            {history.salary_change}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{history.reason_display}</TableCell>
                        <TableCell className="text-muted-foreground">{history.created_by}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => openEditSalary(history)}>
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-sm font-medium">No salary history</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  This user does not have any salary history records yet.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Time Off Requests */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Time Off Requests</CardTitle>
            <CardAction>
              <Button variant="link" size="sm" className="h-auto p-0" asChild>
                <a href={`/admin/time_off_requests?user_id=${user.id}`}>View All</a>
              </Button>
            </CardAction>
          </CardHeader>
          <CardContent>
            {user.recent_requests.length > 0 ? (
              <div className="space-y-3">
                {user.recent_requests.map((request) => (
                  <div
                    key={request.id}
                    className="flex items-center justify-between border-b pb-3 last:border-b-0 last:pb-0"
                  >
                    <div>
                      <p className="text-sm font-medium capitalize">
                        {request.request_type} - {request.days_requested} days
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {request.start_date} - {request.end_date}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary" className={statusColors[request.status] || ""}>
                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      </Badge>
                      <Button variant="link" size="sm" className="h-auto p-0 text-xs" asChild>
                        <a href={`/admin/time_off_requests/${request.id}`}>View</a>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-4 text-center text-sm text-muted-foreground">No requests submitted yet.</p>
            )}
          </CardContent>
        </Card>

        {/* Historic Cost by Bill Type */}
        {user.bills_by_type.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Historic Cost by Bill Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {user.bills_by_type.map((bill, i) => (
                  <Card key={i} className="bg-muted/30 py-4 shadow-none">
                    <CardContent className="px-4">
                      <h4 className="mb-2 text-sm font-medium capitalize">{bill.bill_type_display}</h4>
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs uppercase text-muted-foreground">{bill.currency}:</span>
                        <span className="text-lg font-bold tabular-nums">{bill.total}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Balance Modal */}
      <Dialog open={salaryModalOpen} onOpenChange={setSalaryModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSalaryId === null ? "Record Salary Change" : "Edit Salary Change"}</DialogTitle>
            <DialogDescription>
              Finance reports cost each logged hour at the level held on the day it was worked, so a backdated or
              corrected entry will change past reports.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSalarySubmit} className="space-y-4">
            {Object.keys(salaryErrors).length > 0 && (
              <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                <ul className="space-y-1 text-sm text-destructive">
                  {Object.entries(salaryErrors).map(([field, message]) => (
                    <li key={field}>{message}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="effective_date">Effective Date</Label>
                <Input
                  id="effective_date"
                  type="date"
                  value={salaryForm.effective_date}
                  onChange={(e) => setSalaryForm({ ...salaryForm, effective_date: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Seniority Level</Label>
                <Select
                  value={salaryForm.seniority_level_id}
                  onValueChange={(v) => setSalaryForm({ ...salaryForm, seniority_level_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    {seniority_levels.map((level) => (
                      <SelectItem key={level.id} value={String(level.id)}>
                        {level.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contract_hours">Contract Hours / Day</Label>
                <Input
                  id="contract_hours"
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="24"
                  value={salaryForm.contract_hours}
                  onChange={(e) => setSalaryForm({ ...salaryForm, contract_hours: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bonus">Bonus</Label>
                <Input
                  id="bonus"
                  type="number"
                  step="0.01"
                  min="0"
                  value={salaryForm.bonus}
                  onChange={(e) => setSalaryForm({ ...salaryForm, bonus: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">Reason</Label>
              <Input
                id="reason"
                value={salaryForm.reason}
                onChange={(e) => setSalaryForm({ ...salaryForm, reason: e.target.value })}
                placeholder="Promotion, annual review, correction..."
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={salaryForm.notes}
                onChange={(e) => setSalaryForm({ ...salaryForm, notes: e.target.value })}
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSalaryModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={salaryProcessing}>
                {salaryProcessing ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={balanceModalOpen} onOpenChange={setBalanceModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {balanceModalMode === "create" ? "Add Time Off Balance" : "Edit Time Off Balance"}
            </DialogTitle>
            <DialogDescription>
              {balanceModalMode === "create"
                ? `Add a new time off balance for ${user.name}.`
                : `Update the time off balance for ${user.name}.`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBalanceSubmit}>
            <div className="space-y-4 py-4">
              {Object.keys(balanceErrors).length > 0 && (
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                  <ul className="space-y-1 text-sm text-destructive">
                    {Object.values(balanceErrors).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Balance Type</Label>
                  <Select
                    value={balanceForm.balance_type}
                    onValueChange={(v) => setBalanceForm((prev) => ({ ...prev, balance_type: v }))}
                    disabled={balanceModalMode === "edit"}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BALANCE_TYPES.map((type) => (
                        <SelectItem key={type} value={type} className="capitalize">
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="balance_year">Year</Label>
                  <Input
                    id="balance_year"
                    type="number"
                    min="2001"
                    max="2100"
                    value={balanceForm.year}
                    onChange={(e) => setBalanceForm((prev) => ({ ...prev, year: e.target.value }))}
                    disabled={balanceModalMode === "edit"}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="days_available">Days Available</Label>
                  <Input
                    id="days_available"
                    type="number"
                    min="0"
                    step="0.5"
                    value={balanceForm.days_available}
                    onChange={(e) => setBalanceForm((prev) => ({ ...prev, days_available: e.target.value }))}
                    placeholder="e.g., 15"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="days_used">Days Used</Label>
                  <Input
                    id="days_used"
                    type="number"
                    min="0"
                    step="0.5"
                    value={balanceForm.days_used}
                    onChange={(e) => setBalanceForm((prev) => ({ ...prev, days_used: e.target.value }))}
                    placeholder="e.g., 0"
                    required
                  />
                </div>
              </div>

              {balanceForm.days_available && (
                <div className="rounded-lg bg-muted/50 p-3 text-sm">
                  <span className="text-muted-foreground">Remaining: </span>
                  <span className="font-semibold">
                    {Math.max(
                      0,
                      Number.parseFloat(balanceForm.days_available || "0") -
                        Number.parseFloat(balanceForm.days_used || "0"),
                    )}{" "}
                    days
                  </span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setBalanceModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={balanceProcessing}>
                {balanceProcessing
                  ? balanceModalMode === "create"
                    ? "Creating..."
                    : "Saving..."
                  : balanceModalMode === "create"
                    ? "Create Balance"
                    : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Balance Confirmation Modal */}
      <Dialog open={deleteBalanceId !== null} onOpenChange={(open) => !open && setDeleteBalanceId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Balance</DialogTitle>
            <DialogDescription>Are you sure you want to delete this balance?</DialogDescription>
          </DialogHeader>
          {balanceBeingDeleted && (
            <div className="py-4">
              <div className="rounded-lg border p-3 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium capitalize">{balanceBeingDeleted.balance_type}</span>
                  <span className="text-muted-foreground">{balanceBeingDeleted.year}</span>
                </div>
                <div className="mt-2 flex justify-between text-muted-foreground">
                  <span>
                    {balanceBeingDeleted.days_available} available / {balanceBeingDeleted.days_used} used
                  </span>
                  <span className="font-medium text-foreground">{balanceBeingDeleted.remaining_days} remaining</span>
                </div>
              </div>
              <div className="mt-3 rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-sm text-destructive">
                This action cannot be undone. Any time off requests linked to this balance type and year may be
                affected.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteBalanceId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteBalance} disabled={deleteProcessing}>
              <IconTrash className="mr-2 h-4 w-4" />
              {deleteProcessing ? "Deleting..." : "Delete Balance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Modal */}
      <Dialog open={showDeactivateModal} onOpenChange={setShowDeactivateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate User</DialogTitle>
            <DialogDescription>This will mark {user.name} as inactive.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDeactivate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="last_day_worked">Last Day Worked</Label>
                <Input
                  id="last_day_worked"
                  type="date"
                  value={lastDayWorked}
                  onChange={(e) => setLastDayWorked(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reason">Reason for Leaving</Label>
                <Textarea
                  id="reason"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter the reason for leaving..."
                  required
                />
              </div>
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
                <strong>Warning:</strong> This will mark {user.name} as inactive.
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setShowDeactivateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={processing}>
                {processing ? "Deactivating..." : "Deactivate User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}

AdminUserShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
